import { Fragment, useState, useRef } from 'react'
import { useRouter } from 'next/router'
import { trackGoal } from 'fathom-client'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { htmlToText } from 'html-to-text'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
dayjs.locale('fr')

import { getArticleBySlug, getAllPosts } from '../../lib/api'
import blocksProcessings from 'wordpress/blocksProcessing'

import Navbar from 'components/navbar'
import Layout, { Textlayout } from 'components/layout'
import Content from 'components/article/content'
import Commentform from 'components/article/commentForm'
import Allcomments from 'components/article/allComments'
import Footer from 'components/footer'
import ArticleCard from '@/components/article/card'

import SocialButton from '@/components/social/roundedButton'
import AuthorBox from '@/components/article/authorBox'

export default function articlePost({ article }) {
  const router = useRouter()
  const [replyToCommentId, setReplyToCommentId] = useState('')
  const commentSectionRef = useRef(null)

  if (router.isFallback) {
    return (
      <div className="overflow-hidden">
        <Navbar />
        <main className="relative mt-8 md:mt-16">
          <Layout>
            <div className="mt-8 flex flex-col items-center">
              <div className="h-12 w-11/12 animate-pulse rounded bg-yellow"></div>
              <div className="mt-2 h-12 w-2/3 animate-pulse rounded bg-yellow"></div>
            </div>

            <div className="mt-4 flex flex-col items-end">
              <div className="h-6 w-32 animate-pulse rounded bg-gray-200"> </div>
              <div className="mt-1 flex items-center">
                <div className="h-6 w-64 animate-pulse rounded bg-gray-200"> </div>
              </div>
            </div>

            <Textlayout>
              <div className="mt-4 space-y-2">
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200"> </div>
              </div>

              <div className="mb-6 mt-12">
                <div className="h-8 w-1/2 animate-pulse rounded bg-gray-200"> </div>
              </div>

              <div className="mt-6 space-y-2">
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-full animate-pulse rounded bg-gray-200"> </div>
                <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200"> </div>
              </div>
            </Textlayout>
          </Layout>
        </main>
        <div className="mt-32">
          <Footer />
        </div>
      </div>
    )
  }

  return (
    <>
      <Head>
        <meta charSet="utf-8" />

        <meta name="robots" content={`${article.seo.metaRobotsNoindex}, ${article.seo.metaRobotsNofollow}`} />

        <title>{article.seo.title}</title>
        <meta name="title" content={article.seo.title} />
        <meta name="description" content={article.seo.metaDesc} />
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_BASEPATH}/blog/${article.slug}`} />
        <meta name="author" content={article.author.node.name} />
        {article.tags.nodes.length > 0 && (
          <meta name="keywords" content={article.tags.nodes.map((thistag) => thistag.name).join(', ')} />
        )}

        {/* Open graph (FB) */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_BASEPATH}/blog/${article.slug}`} />
        <meta property="article:author" content={article.author.node.name} />
        <meta property="article:published_time" content={article.seo.opengraphPublishedTime} />
        <meta property="article:modified_time" content={article.seo.opengraphModifiedTime} />
        {article.tags.nodes.length > 0 && (
          <meta name="article:tag" content={article.tags.nodes.map((thistag) => thistag.name).join(' ')} />
        )}

        {article.seo.opengraphTitle ? (
          <meta property="og:title" content={article.seo.opengraphTitle} />
        ) : (
          <meta property="og:title" content={article.seo.title} />
        )}
        {article.seo.opengraphDescription ? (
          <meta property="og:description" content={article.seo.opengraphDescription} />
        ) : (
          <meta property="og:description" content={article.seo.metaDesc} />
        )}
        {article.seo.opengraphImage ? (
          <>
            <meta property="og:image" content={article.seo.opengraphImage.sourceUrl} />
            <meta property="og:image:alt" content={article.seo.opengraphImage.altText} />
            <meta property="og:image:width" content={article.seo.opengraphImage.mediaDetails.width} />
            <meta property="og:image:height" content={article.seo.opengraphImage.mediaDetails.height} />
          </>
        ) : (
          article.featuredImage && (
            <>
              <meta property="og:image" content={article.featuredImage.node.mediaItemUrl} />
              <meta property="og:image:alt" content={article.featuredImage.node.altText} />
              <meta property="og:image:width" content={article.featuredImage.node.mediaDetails.width} />
              <meta property="og:image:height" content={article.featuredImage.node.mediaDetails.height} />
            </>
          )
        )}

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_BASEPATH}/blog/${article.slug}`} />
        {article.author.node.seo.social.twitter && (
          <meta property="twitter:creator" content={`@${article.author.node.seo.social.twitter}`} />
        )}
        {article.seo.twitterTitle ? (
          <meta property="twitter:title" content={article.seo.twitterTitle} />
        ) : (
          <meta property="twitter:title" content={article.seo.title} />
        )}
        {article.seo.twitterTitle ? (
          <meta property="twitter:description" content={article.seo.twitterDescription} />
        ) : (
          <meta property="twitter:description" content={article.seo.metaDesc} />
        )}
        {article.seo.twitterImage?.mediaDetails ? (
          <>
            <meta property="twitter:image" content={article.seo.twitterImage.sourceUrl} />
            <meta property="twitter:image:alt" content={article.seo.twitterImage.altText} />
            <meta property="twitter:image:width" content={article.seo.twitterImage.mediaDetails.width} />
            <meta property="twitter:image:height" content={article.seo.twitterImage.mediaDetails.height} />
          </>
        ) : (
          article.featuredImage && (
            <>
              <meta property="twitter:image" content={article.featuredImage.node.mediaItemUrl} />
              <meta property="twitter:image:alt" content={article.featuredImage.node.altText} />
              <meta property="twitter:image:width" content={article.featuredImage.node.mediaDetails.width} />
              <meta property="twitter:image:height" content={article.featuredImage.node.mediaDetails.height} />
            </>
          )
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              author: {
                '@type': 'Person',
                name: article.author.node.name,
                sameAs: Object.keys({ ...article.author.node.seo.social, website: article.author.node.url })
                  .filter((thisKey) => !!article.author.node.seo.social[thisKey])
                  .map((thisSocial) => {
                    const thisSocialContent = article.author.node.seo.social[thisSocial]
                    if (thisSocial === 'twitter') return `https://twitter.com/${thisSocialContent}`
                    return thisSocialContent
                  })
              },
              publisher: {
                '@id': 'https://ateliermilky.com#identity'
              },
              copyrightHolder: {
                '@id': 'https://ateliermilky.com#identity'
              },
              copyrightYear: `${new Date(article.seo.opengraphPublishedTime).getFullYear()}`,
              name: article.seo.title,
              headline: article.seo.title,
              mainEntityOfPage: `${process.env.NEXT_PUBLIC_BASEPATH}/blog/${article.slug}`,
              dateCreated: article.seo.opengraphPublishedTime,
              datePublished: article.seo.opengraphPublishedTime,
              dateModified: article.seo.opengraphModifiedTime,
              keywords:
                article.tags.nodes.length > 0 ? article.tags.nodes.map((thistag) => thistag.name).join(' ') : '',
              image: article.featuredImage
                ? {
                    '@type': 'ImageObject',
                    url: article.featuredImage.node.mediaItemUrl,
                    description: article.featuredImage.node.altText,
                    width: article.featuredImage.node.mediaDetails.width,
                    height: article.featuredImage.node.mediaDetails.height
                  }
                : ''
            })
          }}
        />
      </Head>
      <div className="">
        <Navbar />
        <main className="relative mt-8 md:mt-16">
          <Layout>
            <article>
              <h1 className="mt-8 text-center font-serif text-3xl font-medium leading-8 text-yellow md:text-5xl">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-col items-end">
                <div className="flex flex-wrap items-center justify-end">
                  {article.categories.nodes.map((thisCategorie, index, array) => (
                    <Fragment key={thisCategorie.id}>
                      <div className="uppercase">
                        <Link href={`/blog/categorie/${thisCategorie.slug}`}>
                          <a className="p-2 hover:underline focus:outline-none focus:ring-1 focus:ring-gray-900">
                            {thisCategorie.name}
                          </a>
                        </Link>
                      </div>
                      {index < array.length - 1 && <div className="h-1 w-1 rounded-full bg-gray-500"></div>}
                    </Fragment>
                  ))}
                </div>
                <div className="mt-1 flex items-center">
                  <div className="text-sm font-medium">Publié le {dayjs(article.date).format('DD MMMM YYYY')}</div>
                </div>
              </div>
              {article.featuredImage && (
                <div className="mt-4">
                  <Image
                    src={article.featuredImage.node.mediaItemUrl}
                    layout="responsive"
                    width={article.featuredImage.node.mediaDetails.width}
                    height={article.featuredImage.node.mediaDetails.height}
                    alt={article.featuredImage.node.altText}
                  />
                </div>
              )}
              <Textlayout>
                <div className="relative">
                  <div className="absolute -left-16 top-4 hidden h-full md:block">
                    <div className="sticky top-8">
                      <div className="relative">
                        <div>
                          <SocialButton
                            socialMedia="facebook"
                            link={`https://www.facebook.com/sharer/sharer.php?u=${process.env.NEXT_PUBLIC_BASEPATH}/blog/${article.slug}`}
                            onClick={() => trackGoal('2BHCKRAQ', 0)}
                            title="Partager l'article sur Facebook"
                          />
                        </div>
                        <div className="mt-2">
                          <SocialButton
                            socialMedia="twitter"
                            link={`https://twitter.com/intent/tweet?text=${encodeURI(
                              article.title + ' ' + process.env.NEXT_PUBLIC_BASEPATH + '/blog/' + article.slug
                            )}`}
                            onClick={() => trackGoal('MJPCTA7J', 0)}
                            title="Partager l'article sur Twitter"
                          />
                        </div>
                        <div className="mt-2">
                          <SocialButton
                            socialMedia="linkedin"
                            link={`https://www.linkedin.com/sharing/share-offsite/?url=${process.env.NEXT_PUBLIC_BASEPATH}/blog/${article.slug}`}
                            onClick={() => trackGoal('2SRXNJSB', 0)}
                            title="Partager l'article sur Linkedin"
                          />
                        </div>

                        <div className="absolute top-[30%] -left-12 -rotate-90 transform">Partager</div>
                      </div>
                    </div>
                  </div>
                  <div className="articleContent mt-4 text-lg">
                    <Content
                      blocks={article.blocks}
                      completeSlug={process.env.NEXT_PUBLIC_BASEPATH + '/blog/' + article.slug}
                    />
                  </div>
                  <div className="flex flex-wrap">
                    {article.tags.nodes.length > 0 &&
                      article.tags.nodes.map((thisTag) => (
                        <div key={thisTag.id} className="mr-2 uppercase tracking-wider">
                          #{thisTag.name}
                        </div>
                      ))}
                  </div>
                  {article.show_author_box.showAuthorBox ? (
                    <div className="mt-8">
                      <AuthorBox authorData={article.author} />
                    </div>
                  ) : null}
                </div>
              </Textlayout>
            </article>
            <div className="mt-12">
              <Textlayout>
                <h2 className="font-serif text-4xl font-medium">À lire ensuite</h2>
              </Textlayout>
              <ul className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                {article.relatedPosts.nodes.map((thisArticle) => (
                  <li key={thisArticle.id}>
                    <ArticleCard
                      key={thisArticle.id}
                      slug={thisArticle.slug}
                      featuredImage={thisArticle.featuredImage}
                      title={thisArticle.title}
                      excerpt={thisArticle.excerpt}
                    />
                  </li>
                ))}
              </ul>
            </div>
            <div id="comment" ref={commentSectionRef} className="mt-12 border-t border-blue-200 pt-8">
              <Textlayout>
                <h2 className="font-serif text-4xl font-medium">Commentaires</h2>
              </Textlayout>
              <div className="mt-4">
                <Commentform
                  articleId={article.databaseId}
                  inReplyTo={replyToCommentId}
                  setInReplyTo={setReplyToCommentId}
                  comments={article.comments}
                />
              </div>
              <div className="mt-4">
                <Textlayout>
                  <Allcomments
                    replyTo={(commentId) => {
                      setReplyToCommentId(commentId)
                      commentSectionRef.current.scrollIntoView()
                    }}
                    comments={article.comments}
                  />
                </Textlayout>
              </div>
            </div>
          </Layout>
        </main>
        <div className="mt-32">
          <Footer />
        </div>
      </div>
    </>
  )
}

export async function getStaticProps({ params }) {
  // try {
  const article = await getArticleBySlug(params.slug)

  if (!article) return { notFound: true }
  const articleHydrated = await blocksProcessings(article)

  let allCommentsThatAreReplies = [...article.comments.edges.filter((thisComment) => thisComment.node.parentId)]

  const articleComments = article.comments.edges
    .filter((thisComment) => !thisComment.node.parentId)
    .map((thisComment) => {
      const child = {}

      let indicator = 0
      for (let index = 0; index <= allCommentsThatAreReplies.length ** 2; index++) {
        /*console.log(
            `Loop required: ${
              allCommentsThatAreReplies.length ** 2
            }. Current loop: ${index}. Indicator: ${indicator}. Indicator max: ${allCommentsThatAreReplies.length}`
          )*/
        const element = allCommentsThatAreReplies[index % allCommentsThatAreReplies.length]

        if ([...Object.keys(child), thisComment.node.id].indexOf(element.node.parentId) > -1) {
          indicator = 0
          child[element.node.id] = element
        }

        if (indicator >= allCommentsThatAreReplies.length) break

        indicator += 1
      }

      // Retirer de allCommentsThatAreReplies ceux pris
      Object.keys(child).map((thisIdThatNeedToBeRemovedFromPotentialReplies) => {
        const index = allCommentsThatAreReplies.indexOf(child[thisIdThatNeedToBeRemovedFromPotentialReplies])
        //console.log('To delete', index)

        if (index > -1) {
          allCommentsThatAreReplies.splice(index, 1)
        }
      })

      return {
        node: {
          ...thisComment.node,
          content: htmlToText(thisComment.node.content),
          childs: Object.keys(child).map((thisChild) => {
            const thisFullChild = child[thisChild]
            return {
              node: { ...thisFullChild.node, content: htmlToText(thisFullChild.node.content) }
            }
          })
        }
      }
    })

  return {
    props: {
      article: { ...articleHydrated, comments: articleComments }
    },
    revalidate: 300 // 5 min
  }
  // } catch (e) {
  //   console.error('An error occured while getting article props', e)
  //   return {
  //     notFound: true
  //   }
  // }
}

export async function getStaticPaths() {
  const posts = await getAllPosts()

  return {
    paths: posts.nodes.map((thisPost) => ({ params: { slug: thisPost.slug } })),
    fallback: true
  }
}
